import React, { useCallback, useReducer, useRef } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'


import Button from 'components/shared/Button'
import { ReactComponent as Arrow } from 'components/shared/Select/arrow.svg'
import usePanelAnimations from 'hooks/usePanelAnimations'

import classnames from 'classnames/bind'
import { Text } from 'components/typo'
import { ReactComponent as CheckIcon } from './checkIcon.svg'

import css from './styles.module.scss'

const cx = classnames.bind(css)

function reducer(prevState, { type, payload }) {
  switch (type) {
    case 'toggle':
      return { ...prevState, isOpen: !prevState.isOpen }
    case 'setFocusedOption':
      return { ...prevState, focusedOptionIndex: payload }
    case 'setCurrentOption':
      return {
        ...prevState,
        isOpen: false,
        focusedOptionIndex: null,
        currentOption: payload,
      }
    case 'setFocusedOption':
      return { ...prevState, focusedOptionIndex: payload }
    case 'setCheckedOptions':
      var { option, isOpen = true } = payload
      const isChecked = prevState.checkedOptions.some(
        (el) => el.label === option.label
      )
      let options = []

      if (isChecked) {
        options = prevState.checkedOptions.filter(
          (el) => el.label !== option.label
        )
      } else {
        options = [...prevState.checkedOptions, option]
      }

      return {
        ...prevState,
        isOpen,
        checkedOptions: options,
      }
  }
}

function useClickOutsideRef(enable, fn, userRef) {
  const localRef = useRef()
  const fnRef = useRef()

  fnRef.current = fn
  const elRef = userRef || localRef

  const handle = useCallback(
    (e) => {
      const isTouch = e.type === 'touchstart'

      if (e.type === 'click' && isTouch) {
        return
      }
      const el = elRef.current

      if (el && !el.contains(e.target)) fnRef.current(e)
    },
    [elRef]
  )

  useIsomorphicLayoutEffect(() => {
    if (enable) {
      document.addEventListener('touchstart', handle, true)
      document.addEventListener('click', handle, true)
    }

    return () => {
      document.removeEventListener('touchstart', handle, true)
      document.removeEventListener('click', handle, true)
    }
  }, [enable, handle])
}

const MultiSelect = ({
  className,
  facetKey,
  options,
  onChange,
  checkbox,
  name,
}) => {
  const componentRef = useRef()
  const panelRef = useRef()
  const backgroundRef = useRef()
  const contentRef = useRef()
  const firstRender = useRef(true)

  const [
    {
      isOpen,
      checkedOptions,
      currentOption: { value, label },
      focusedOptionIndex,
    },
    dispatch,
  ] = useReducer(reducer, {
    isOpen: false,
    checkedOptions: [],
    currentOption: options && options.length > 0 ? options[0] : '',
    focusedOptionIndex: null,
  })
  const close = useCallback(() => {
    dispatch({ type: 'toggle' })
  }, [])

  useClickOutsideRef(isOpen, close, componentRef)

  usePanelAnimations(isOpen, {
    panelRef,
    backgroundRef,
    contentRef,
  })

  const onKeyDown = (e) => {
    if (e.keyCode === 13 && (focusedOptionIndex || focusedOptionIndex === 0)) {
      dispatch({
        type: 'setCurrentOption',
        payload: options[focusedOptionIndex],
      })
    }

    if (
      (e.keyCode === 9 && focusedOptionIndex === options.length - 1) ||
      e.keyCode === 27
    ) {
      dispatch({ type: 'toggle' })
    }
  }

  useIsomorphicLayoutEffect(() => {
    if (!firstRender.current && !checkbox) {
      onChange && onChange(value)
    }
  }, [checkedOptions])

  useIsomorphicLayoutEffect(() => {
    if (!firstRender.current && checkbox) {
      onChange && onChange(facetKey, checkedOptions)
    } else {
      if (!firstRender.current) {
        onChange && onChange(value)
      }
    }
  }, [value, checkedOptions])

  useIsomorphicLayoutEffect(() => {
    firstRender.current = false
  }, [])

  return (
    <div
      onKeyDown={onKeyDown}
      ref={componentRef}
      className={cx(className, css.MultiSelect, { isOpen })}
    >
      <Button
        onClick={() => dispatch({ type: 'toggle' })}
        tagName="button"
        className={css.buttonContainer}
        buttonClassName={cx(css.button, { active: isOpen })}
        buttonWrapperClassName={cx(css.buttonWrapper, { active: isOpen })}
        theme="gray"
        withFocus={isOpen}
      >
        <Text size="smaller" tag="div" theme="bay-blue" className={css.label}>
          {checkbox ? name : label}
        </Text>
        <Arrow className={css.arrow} />
      </Button>
      <div ref={panelRef} className={css.list}>
        <div ref={backgroundRef} className={css.listBackground} />
        <ul ref={contentRef} className={css.options} role="listbox">
          {options &&
            options.length > 0 &&
            options.map((option, index) => (
              <Text
                key={index}
                tag="li"
                theme="gray"
                size="small"
                className={cx(css.option, {
                  checked: checkedOptions.some(
                    (el) => el.label === option.label
                  ),
                  focused: focusedOptionIndex === index,
                })}
                role="option"
                tabIndex="0"
                onFocus={() =>
                  dispatch({ type: 'setFocusedOption', payload: index })
                }
                onBlur={() =>
                  !checkbox &&
                  dispatch({ type: 'setFocusedOption', payload: null })
                }
                onMouseDown={() => {
                  checkbox
                    ? dispatch({
                        type: 'setCheckedOptions',
                        payload: { option },
                      })
                    : dispatch({ type: 'setCurrentOption', payload: option })
                }}
              >
                {checkbox && (
                  <span className={css.box}>
                    <CheckIcon className={css.checkIcon} />
                  </span>
                )}
                {option.label}
              </Text>
            ))}
        </ul>
      </div>
    </div>
  )
}

MultiSelect.defaultProps = {}

export default MultiSelect
